import { useState, useCallback, useEffect } from "react";
import { memoryStore, setItemImmediate } from "../utils/storage";

// Send log for snapshot-report emails (bulk monthly + single sends).
// Shape: { [clientId (ת"ז)]: {
//   lastSentAt: ISO, email, clientName,
//   history: [{ sentAt: ISO, email, kind: 'bulk'|'single', subject }]  // newest first
// } }
// Keyed by the stable national ID (same key clientOverrides uses) — the
// unified list id is file-derived and changes between imports.
const LOG_KEY = "pension_report_log_v1";

// Cap per-client history so the log can't grow unbounded (3 years of monthly
// sends still fit).
const HISTORY_CAP = 36;

function loadAll() {
  try {
    const raw = memoryStore.getItem(LOG_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

// True if this log entry is from the current calendar month.
export function wasSentThisMonth(entry) {
  if (!entry?.lastSentAt) return false;
  const sent = new Date(entry.lastSentAt);
  const now = new Date();
  return sent.getFullYear() === now.getFullYear() && sent.getMonth() === now.getMonth();
}

export function useReportSendLog() {
  const [log, setLog] = useState(loadAll);

  useEffect(() => {
    const onSync = () => setLog(loadAll());
    window.addEventListener("pension-data-synced", onSync);
    return () => window.removeEventListener("pension-data-synced", onSync);
  }, []);

  // Immediate (non-debounced) persist: each successful send must be durable
  // in Supabase before the next one starts, so a closed tab mid-run doesn't
  // lose progress and re-send to the same clients.
  const markSent = useCallback((clientId, email, { clientName, kind = "bulk", subject, period } = {}) => {
    setLog(prev => {
      const now = new Date().toISOString();
      const existing = prev[clientId] || {};
      const history = [
        // period = the report month (YYYY-MM) — used to regenerate the report
        // from that month's holdings snapshot when viewing history.
        { sentAt: now, email, kind, subject: subject || "", period: period || now.slice(0, 7) },
        ...(existing.history || []),
      ].slice(0, HISTORY_CAP);
      const next = {
        ...prev,
        [clientId]: {
          lastSentAt: now,
          email,
          clientName: clientName || existing.clientName || "",
          history,
        },
      };
      try { setItemImmediate(LOG_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  return { log, markSent };
}
